import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { ValidationUtils } from "../../../../../Utils";
import { FmTextField, FmText, FmCheckBox, FmButton, FmIcon, FmMessage, FmLoader } from "../../../../../Components";
import { useIsMobile, useTheme } from "../../../../../Hooks";
import AuthState from "../../../../../Redux/States/Auth";

import useStyles from "./LoginForm.style";
import { GeneralState } from "../../../../../Redux";

interface IFormInputs {
  email: string;
  password: string;
  keepMeLoggedIn: boolean;
}

interface LoginFormProps {
  onSubmit: (email: string, password: string, keepMeLoggedIn: boolean) => void;
  loginInProgress: boolean;
  error?: string;
  mailApproved: boolean;
  mailConfirmationInProgress: boolean;
  onForgotPasswordClicked: (email: string) => any;
}

const LoginForm = ({ onSubmit, loginInProgress, error, mailApproved, mailConfirmationInProgress, onForgotPasswordClicked }: LoginFormProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { email, password, keepMeLoggedIn } = useSelector(AuthState.Selectors.selectLoginFormData);
  const userWantsToBeRemembered = useSelector(GeneralState.Selectors.selectUserWantsToBeRemembered);
  const {
    handleSubmit,
    getValues,
    control,
    register,
    formState: { errors },
  } = useForm<IFormInputs>({
    mode: "onSubmit",
    defaultValues: {
      email: email,
      password: password,
      keepMeLoggedIn: userWantsToBeRemembered,
    },
  });
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [passwordIsVisible, setPasswordIsVisible] = useState<boolean>(false);
  const [showMessage, setShowMessage] = useState<boolean>(false);

  const handleOnSubmit: SubmitHandler<IFormInputs> = (data) => {
    onSubmit && onSubmit(data.email, data.password, !!data.keepMeLoggedIn);
  };

  const handleForgetPassword = async (email: string) => {
    const response = await onForgotPasswordClicked(email);

    if (response && response.statusCode === 200) {
      setShowMessage(true);
    }
  };

  const showErrorMessage = !loginInProgress && !mailConfirmationInProgress && !!error;
  const showMailApprovedMessage = mailApproved && !showErrorMessage;

  useEffect(() => {
    return () => {
      dispatch(
        AuthState.Actions.setLoginFormData({
          email: getValues("email"),
          password: getValues("password"),
          //keepMeLoggedIn: getValues('keepMeLoggedIn'),
          keepMeLoggedIn: true,
        }),
      );
    };
  }, [dispatch]);

  const closeMessageHandler = () => {
    setShowMessage(false);
  };

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)} className={classes.container}>
      <div className={classes.input}>
        <Controller
          name={"email"}
          control={control}
          rules={{
            required: {
              value: true,
              message: t("validationErrors.fieldIsRequired"),
            },
            pattern: {
              value: ValidationUtils.StringRegex.EMAIL,
              message: t("validationErrors.incorrectEmailFormat"),
            },
          }}
          render={({ field }) => {
            return (
              <FmTextField
                {...register("email")}
                {...field}
                label={t("signIn.email")}
                variant="filled"
                error={errors.email}
                value={field.value}
                inputProps={{
                  icon: <FmIcon url={theme.images.mail} title={"email"} />,
                }}
                helperText={errors.email?.message || ""}
              />
            );
          }}
        />
      </div>
      <div className={classes.input}>
        <Controller
          name={"password"}
          control={control}
          rules={{
            required: {
              value: true,
              message: t("validationErrors.fieldIsRequired"),
            },
            minLength: {
              value: 8,
              message: t("validationErrors.passwordValidation"),
            },
          }}
          render={({ field }) => {
            return (
              <FmTextField
                {...register("password")}
                {...field}
                label={t("signIn.password")}
                variant="filled"
                type={passwordIsVisible ? "text" : "password"}
                error={errors.password}
                displayIcon={true}
                onIconClick={() => setPasswordIsVisible((visible: boolean) => !visible)}
                value={field.value}
                inputProps={{
                  icon: <FmIcon url={theme.images.eye} title={"visible"} />,
                }}
                helperText={errors.password?.message || ""}
              />
            );
          }}
        />
      </div>
      <FmText onClick={() => handleForgetPassword(getValues("email"))} className={classes.link}>
        {t("signIn.forgotPassword")}
      </FmText>
      <div className={classes.termsBox}>
        <div className={classes.checkboxContainer}>
          <FmCheckBox isChecked={() => getValues("keepMeLoggedIn")} control={control} register={register} name="keepMeLoggedIn" required={false} />
        </div>
        <FmText>{isMobile ? t("signIn.keepMeLoggedInMobile") : t("signIn.keepMeLoggedIn")}</FmText>
      </div>
      {showErrorMessage && (
        <FmMessage className={classes.message} variant={"failure"}>
          {error}
        </FmMessage>
      )}
      {showMailApprovedMessage && (
        <FmMessage className={classes.message} variant={"success"}>
          {t("signIn.emailApproved")}
        </FmMessage>
      )}
      {mailConfirmationInProgress && (
        <div className={classes.loaderContainer}>
          <FmLoader size={"verySmall"} />
        </div>
      )}
      <div className={classes.buttonContainer}>
        <FmButton type="submit" fullWidth="mobileFullWidth" loading={loginInProgress} disabled={loginInProgress}>
          {t("authorizationTabs.signIn")}
        </FmButton>
      </div>
      {showMessage && (
        <div className={classes.messageContainer}>
          <FmMessage closeMessage={closeMessageHandler}>{t("updatePassword.sendEmail")}</FmMessage>
        </div>
      )}
    </form>
  );
};

export default LoginForm;
